// ============================================
// F-06 扫码登录（新端）：申请 ticket → 展示二维码 → 轮询领取登录态
// 流程：本页（新设备）展示二维码；已登录设备打开 /qr-confirm?ticket= 确认；
//       本页轮询到 confirmed 后写入会话并跳转。
// ============================================
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, Typography, Spin, Result, Space, message } from 'antd';
import { QrcodeOutlined, ReloadOutlined } from '@ant-design/icons';
import { issueQrLogin, pollQrLogin } from '../api/qrLogin';
import useAuthStore from '../stores/authStore';

const { Title, Text } = Typography;

export default function QrLoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect') || '/projects';

  const [ticket, setTicket] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [status, setStatus] = useState<'pending' | 'scanned' | 'confirmed' | 'expired'>('pending');
  const [left, setLeft] = useState(60);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const finishedRef = useRef(false);

  const clearTimers = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const start = useCallback(async () => {
    clearTimers();
    finishedRef.current = false;
    setLoading(true);
    setError('');
    setQrDataUrl('');
    setStatus('pending');
    try {
      const { ticket: tk, expiresIn } = await issueQrLogin();
      setTicket(tk);
      setLeft(expiresIn || 60);
      const content = `${window.location.origin}/qr-confirm?ticket=${tk}`;
      try {
        const QR = await import('qrcode');
        const url = await (QR as any).toDataURL(content, { width: 220, margin: 1, color: { dark: '#e6f4ff', light: '#141428' } });
        setQrDataUrl(url);
      } catch {
        setQrDataUrl('');
      }
      timerRef.current = setInterval(() => {
        setLeft((v) => {
          if (v <= 1) {
            clearTimers();
            setStatus('expired');
            return 0;
          }
          return v - 1;
        });
      }, 1000);
      pollRef.current = setInterval(async () => {
        if (finishedRef.current) return;
        try {
          const res = await pollQrLogin(tk);
          if (res.status === 'scanned') setStatus('scanned');
          if (res.status === 'expired') { clearTimers(); setStatus('expired'); }
          if (res.status === 'confirmed') {
            finishedRef.current = true;
            clearTimers();
            setStatus('confirmed');
            useAuthStore.getState().login(res.user as any, res.token);
            message.success('扫码登录成功');
            setTimeout(() => navigate(redirect, { replace: true }), 600);
          }
        } catch {
          /* 网络抖动忽略，下一轮继续 */
        }
      }, 2000);
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || '二维码生成失败';
      setError(String(msg));
    } finally {
      setLoading(false);
    }
  }, [navigate, redirect]);

  useEffect(() => {
    start();
    return clearTimers;
  }, [start]);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f0f1a' }}>
      <Card style={{ width: 380, background: '#1a1a2e', borderColor: '#30475e' }} styles={{ body: { padding: 24 } }}>
        <Space orientation="vertical" size={14} style={{ width: '100%', textAlign: 'center' }}>
          <Title level={4} style={{ margin: 0, color: '#e6f4ff' }}>
            <QrcodeOutlined style={{ marginRight: 8 }} />
            扫码登录
          </Title>
          <Text style={{ color: '#888', fontSize: 12 }}>
            请使用已登录的设备（手机/平板/电脑）扫码，或直接打开链接确认
          </Text>

          {error ? (
            <Result status="error" title="生成失败" subTitle={error} />
          ) : status === 'confirmed' ? (
            <Result status="success" title="登录成功" subTitle="正在进入系统…" />
          ) : status === 'expired' ? (
            <Result
              status="warning"
              title="二维码已过期"
              subTitle="请刷新后重新扫码"
              extra={<Button type="primary" icon={<ReloadOutlined />} onClick={start}>刷新二维码</Button>}
            />
          ) : (
            <>
              <div
                data-testid="qr-code-box"
                style={{
                  width: 240, height: 240, margin: '0 auto', background: '#141428',
                  border: '1px solid #30475e', borderRadius: 8,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative',
                }}
              >
                {loading || !qrDataUrl ? <Spin /> : <img src={qrDataUrl} alt="扫码登录二维码" width={220} height={220} />}
                {status === 'scanned' && (
                  <div style={{
                    position: 'absolute', inset: 0, background: 'rgba(20,20,40,.82)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 8,
                  }}>
                    <Spin />
                    <Text style={{ color: '#52c41a' }} data-testid="qr-scanned-tip">已扫码，请在设备上确认</Text>
                  </div>
                )}
              </div>
              <Text style={{ color: left <= 10 ? '#ff7875' : '#888', fontSize: 12 }} data-testid="qr-countdown">
                二维码 {left}s 后过期 · 状态：{status === 'scanned' ? '待确认' : '等待扫码'}
              </Text>
              <span data-testid="qr-ticket" style={{ display: 'none' }}>{ticket}</span>
              <Space>
                <Button onClick={start} icon={<ReloadOutlined />}>刷新</Button>
                <Button type="link" onClick={() => navigate('/login')}>返回登录</Button>
              </Space>
            </>
          )}
        </Space>
      </Card>
    </div>
  );
}
